import { useState, useEffect } from 'react';
import { MapPin, Calendar, CloudRain, Users, Clock, ChevronUp, Briefcase, Building, ChevronLeft, ChevronRight, ExternalLink, Search, Menu, X, Globe, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../StoreContext/LanguageContext'; // Import language hook


// Import your Login component
import Login from '../Pages/Login';

export default function AboutBuldhanaPage() {
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLanguage, setShowLanguage] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { language, changeLanguage, translations } = useLanguage();
  const languageCodeMap = {
    'EN': 'en',
    'HI': 'hi',
    'MR': 'mr',
    'ES': 'es'
  };

  const toggleLoginPopup = () => {
    setShowLoginPopup(!showLoginPopup);
  };

  const handleCloseLogin = () => {
    setShowLoginPopup(false);
  };


  // Track scrolling for header styling and section highlighting
  useEffect(() => {
    const handleScroll = () => {
      // Header styling
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // Determine active section based on scroll position
      const sections = ['about-section', 'economy-section', 'admin-section', 'gallery-section', 'footer-section'];
      let currentSection = '';

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            currentSection = section;
          }
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Smooth scroll function for navigation
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // Adjust offset to account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Close mobile menu after clicking a link
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    }
  };

  // Gallery images
  const galleryImages = [
    { src: "/home image/ChatGPT Image May 15, 2025, 02_09_00 PM.png", alt: "Lonar Crater Lake", title: <span>{translations[language].LonarCraterLake}</span> },
    { src: "/home image/IMG_20250515_143303.jpg", alt: "Shegaon Temple", title: <span>{translations[language].ShegaonTemple}</span> },
    { src: "/home image/Screenshot (67).png", alt: "Sindkhed Raja Fort", title: <span>{translations[language].SindkhedRajaFort}</span> },
    { src: "/home image/Screenshot (70).png", alt: "Buldhana City", title: <span>{translations[language].BuldhanaCity}</span> },
  ];

  // Handler for moving carousel slides
  const handleSlideChange = (index) => {
    setCurrentSlide(index);
  }
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === 0 ? galleryImages.length - 1 : prevSlide - 1
    );
  };

  // Auto-rotate gallery
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [currentSlide]);

  // Historical timeline data
  const historyTimeline = [
    { year: "Pre-Historic", event: "Evidence of early settlements around Lonar Lake" },
    { year: "2nd Century BC", event: "Part of the Satavahana Empire" },
    { year: "6th-7th Century", event: "Ruled by the Chalukya dynasty" },
    { year: "13th Century", event: "Under the Delhi Sultanate" },
    { year: "16th-17th Century", event: "Part of the Mughal Empire" },
    { year: "18th Century", event: "Control by Maratha Empire" },
    { year: "1853", event: "Incorporated into British India" },
    { year: "1960", event: "Became part of Maharashtra state after India's independence" }
  ];

  // Administrative divisions
  const tehsils = [
    <span>{translations[language].Buldhana}</span>,
    <span>{translations[language].Chikhli}</span>,
    <span>{translations[language].DeulgaonRaja}</span>,
    <span>{translations[language].JalgaonJamod}</span>,
    <span>{translations[language].Malkapur}</span>,
    <span>{translations[language].Mehkar}</span>,
    <span>{translations[language].Motala}</span>,
    <span>{translations[language].Nandura}</span>,
    <span>{translations[language].Sangrampur}</span>,
    <span>{translations[language].Shegaon}</span>,
    <span>{translations[language].SindkhedRaja}</span>,
    <span>{translations[language].Khamgaon}</span>,
    <span>{translations[language].Lonar}</span>,

  ];

  const handleLanguageChange = (code) => {
    const contextLangCode = languageCodeMap[code] || 'en';
    changeLanguage(contextLangCode);
    setShowLanguage(false);
  };

  // Get the current language code for display
  const getCurrentLanguageCode = () => {
    // Convert context language to UI language code
    for (const [uiCode, contextCode] of Object.entries(languageCodeMap)) {
      if (contextCode === language) {
        return uiCode;
      }
    }
    return 'EN'; // Default
  };

  const languageOptions = [
    { code: 'EN', name: 'English', flag: '🇺🇸' },
    { code: 'HI', name: 'हिंदी', flag: 'hi' },
    { code: 'MR', name: 'मराठी', flag: 'mr' },
    // { code: 'ES', name: 'Español', flag: '🇪🇸' }
  ];

  return (
    <div className="flex flex-col min-h-screen font-sans bg-gradient-to-b from-white to-amber-50">
      {/* Header */}
      <header className={`transition-all fixed top-0 left-0 w-full duration-500 z-30 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-white shadow-md'}`}>
        <div className="mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center p-3">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3 mb-3 md:mb-0">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center overflow-hidden transform transition hover:scale-105 duration-300 shadow-md">
                <img src="\home image\buldana image copy.svg" alt="Buldhana Logo" className="w-full h-full object-cover" />
              </div>
              <div className="animate-fadeIn">
                <h2 className="text-gray-800 font-bold text-lg group">
                  <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">{translations[language].BuldhanaDistrict}</span>
                </h2>
                <p className="text-xs text-gray-500">{translations[language].GovernmentOfMaharashtra}</p>
              </div>
            </div>

            {/* Desktop buttons */}
            <div className="hidden md:flex space-x-3">
              {/* Language Switcher - Only the icon */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowLanguage(!showLanguage);
                  }}
                  className="relative p-2 rounded-full hover:bg-blue-50 focus:outline-none transition-all duration-200 group"
                  aria-label="Switch Language"
                >
                  <Globe className="h-5 w-5 text-gray-600 group-hover:text-blue-600 group-hover:scale-110 transition-all duration-200" />
                  <span className="absolute bottom-0 right-0 h-4 w-4 bg-blue-100 border border-blue-300 rounded-full flex items-center justify-center text-[10px] text-blue-700 font-bold">
                    {getCurrentLanguageCode()}
                  </span>
                </button>

                {/* Language Dropdown */}
                {showLanguage && (
                  <div className="language-dropdown absolute right-0 mt-3 w-48 bg-white rounded-lg shadow-2xl py-2 z-10 border border-gray-200 animate-scaleIn">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <h3 className="font-medium text-gray-800 flex items-center">
                        <Globe className="h-4 w-4 mr-2 text-blue-500" />
                        {translations[language]?.language || 'Language'}
                      </h3>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {languageOptions.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`language-option w-full text-left px-4 py-2 flex items-center space-x-3 hover:bg-blue-50 transition duration-200 ${getCurrentLanguageCode() === lang.code ? 'bg-blue-50 font-medium' : ''}`}
                        >
                          <span className="text-xl">{lang.flag}</span>
                          <span className="text-sm text-gray-700">{lang.name}</span>
                          {getCurrentLanguageCode() === lang.code && (
                            <Check className="h-4 w-4 text-blue-500 ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button className="px-4 py-1.5 border bg-gradient-to-r from-amber-500 to-orange-500 text-white border-amber-600 rounded-full hover:from-amber-600 hover:to-orange-600 transition flex items-center space-x-1 transform hover:scale-105 duration-300 shadow-md" onClick={toggleLoginPopup}>
                <span>{translations[language].Login}</span>
                <Users size={16} />
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleMenu}
                className="p-2 focus:outline-none text-gray-700 hover:text-amber-600 transition-colors duration-300"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:block bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 text-white">
            <ul className="flex justify-center text-sm">
              <li className="group relative">
                <a
                  href="/"
                  className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                >
                  {translations[language].Home}
                </a>
                <div className="absolute left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-amber-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </li>
              <li className={`group relative ${activeSection === 'about-section' ? 'bg-gray-700' : ''}`}>
                <a
                  href="#about-section"
                  className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('about-section');
                  }}
                >
                  {translations[language].About}
                </a>
                <div className={`absolute left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-amber-600 transform ${activeSection === 'about-section' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-300 origin-left`}></div>
              </li>
              <li className={`group relative ${activeSection === 'economy-section' ? 'bg-gray-700' : ''}`}>
                <a
                  href="#economy-section"
                  className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('economy-section');
                  }}
                >
                  {translations[language].Economy}
                </a>
                <div className={`absolute left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-amber-600 transform ${activeSection === 'economy-section' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-300 origin-left`}></div>
              </li>
              <li className={`group relative ${activeSection === 'admin-section' ? 'bg-gray-700' : ''}`}>
                <a
                  href="#admin-section"
                  className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('admin-section');
                  }}
                >
                  {translations[language].Administration}
                </a>
                <div className={`absolute left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-amber-600 transform ${activeSection === 'admin-section' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-300 origin-left`}></div>
              </li>
              <li className={`group relative ${activeSection === 'gallery-section' ? 'bg-gray-700' : ''}`}>
                <a
                  href="#gallery-section"
                  className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('gallery-section');
                  }}
                >
                  {translations[language].Gallery}
                </a>
                <div className={`absolute left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-amber-600 transform ${activeSection === 'gallery-section' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-300 origin-left`}></div>
              </li>
              <li className={`group relative ${activeSection === 'footer-section' ? 'bg-gray-700' : ''}`}>
                <a
                  href="#footer-section"
                  className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('footer-section');
                  }}
                >
                  {translations[language].Contact}
                </a>
                <div className={`absolute left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-amber-600 transform ${activeSection === 'footer-section' ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-300 origin-left`}></div>
              </li>
            </ul>
          </nav>

          {/* Mobile Navigation */}
          <nav className={`md:hidden bg-gradient-to-b from-gray-800 to-gray-900 text-white absolute w-full left-0 z-20 transform transition-all duration-500 ease-in-out ${isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
            <ul className="flex flex-col text-sm">
              <li className="border-b border-gray-700">
                <a href="/" className="block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out">
                  {translations[language].Home}
                </a>
              </li>
              <li className="border-b border-gray-700">
                <a
                  href="#about-section"
                  className={`block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out ${activeSection === 'about-section' ? 'bg-gray-700 text-amber-400' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('about-section');
                  }}
                >
                  {translations[language].About}
                </a>
              </li>
              <li className="border-b border-gray-700">
                <a
                  href="#economy-section"
                  className={`block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out ${activeSection === 'economy-section' ? 'bg-gray-700 text-amber-400' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('economy-section');
                  }}
                >
                  {translations[language].Economy}
                </a>
              </li>
              <li className="border-b border-gray-700">
                <a
                  href="#admin-section"
                  className={`block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out ${activeSection === 'admin-section' ? 'bg-gray-700 text-amber-400' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('admin-section');
                  }}
                >
                  {translations[language].Administration}
                </a>
              </li>
              <li className="border-b border-gray-700">
                <a
                  href="#gallery-section"
                  className={`block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out ${activeSection === 'gallery-section' ? 'bg-gray-700 text-amber-400' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('gallery-section');
                  }}
                >
                  {translations[language].Gallery}
                </a>
              </li>
              <li className="border-b border-gray-700 last:border-0">
                <a
                  href="#footer-section"
                  className={`block px-4 py-3 hover:bg-gray-700 transition duration-300 ease-in-out ${activeSection === 'footer-section' ? 'bg-gray-700 text-amber-400' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('footer-section');
                  }}
                >
                  {translations[language].Contact}
                </a>
              </li>
              <li className="p-4 space-y-2">
                <button className="w-full px-4 py-2 border border-gray-300 rounded-full text-white hover:bg-gray-700 transition" onClick={toggleLoginPopup}>
                  {translations[language].Login}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Login Popup */}
      {showLoginPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 relative">
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              onClick={toggleLoginPopup}
            >
              <X size={24} />
            </button>
            <div className="mt-2">
              <Login onLoginSuccess={() => {
                // setShowLoginPopup(false);
                navigate('/Dashboard');
              }} />
              <Login onClose={handleCloseLogin} />
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden" style={{ marginTop: "80px" }}>
        <div className="absolute inset-0">
          <img
            src="/home image/IMG_20250515_111536.jpg"
            alt="Buldhana District Panorama"
            className="w-full h-full object-center transform scale-105 animate-slowZoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/70"></div>
        </div>
        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg animate-fadeInUp">
            {translations[language].AboutBuldhana}
          </h1>
          <p className="text-xl md:text-2xl text-amber-100 max-w-3xl animate-fadeInUp animation-delay-300 drop-shadow-lg">
            {translations[language].BuldhanaTagline}
          </p>
          <div className="mt-8 animate-fadeInUp animation-delay-600">
            <button onClick={() => scrollToSection('about-section')} className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full shadow-lg hover:shadow-xl transition duration-300 transform hover:scale-105">
              {translations[language].Explore}
            </button>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <section id="about-section" className="py-16 px-4 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="md:w-1/2 animate-fadeInLeft">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 inline-block relative">
              <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">{translations[language].DistrictOverview}</span>
              <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-orange-500"></span>
            </h2>
            <p className="text-gray-700 mb-4">
              {translations[language].Buldhanapara}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-1 duration-300 border-l-4 border-amber-500">
                <div className="flex items-center text-amber-600 mb-2">
                  <MapPin size={20} className="mr-2" />
                  <h3 className="font-semibold">{translations[language].Location}</h3>
                </div>
                <p className="text-gray-700">{translations[language].ldes}</p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-1 duration-300 border-l-4 border-amber-500">
                <div className="flex items-center text-amber-600 mb-2">
                  <MapPin size={20} className="mr-2" />
                  <h3 className="font-semibold">{translations[language].Area}</h3>
                </div>
                <p className="text-gray-700">{translations[language].ades}</p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-1 duration-300 border-l-4 border-amber-500">
                <div className="flex items-center text-amber-600 mb-2">
                  <Users size={20} className="mr-2" />
                  <h3 className="font-semibold">{translations[language].Population}</h3>
                </div>
                <p className="text-gray-700">{translations[language].pdes}</p>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition transform hover:-translate-y-1 duration-300 border-l-4 border-amber-500">
                <div className="flex items-center text-amber-600 mb-2">
                  <Building size={20} className="mr-2" />
                  <h3 className="font-semibold">{translations[language].Headquarters}</h3>
                </div>
                <p className="text-gray-700">{translations[language].hdes}</p>
              </div>
            </div>
          </div>

          <div className="md:w-1/2 flex flex-col justify-center animate-fadeInRight">
            <div className="bg-white p-4 rounded-lg shadow-lg transform transition hover:scale-105 duration-500 overflow-hidden">
              <img
                src="/home image/map of buldhana.jpg"
                alt="Map of Buldhana District"
                className="w-full h-auto rounded hover:scale-105 transition duration-1000"
              />
              <p className="text-sm text-center mt-2 text-gray-600 italic">{translations[language].map}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Geography & Climate Section */}
      <section className="py-16 px-4 max-w-7xl mx-auto">
        <h2 className="text-3xl text-center font-bold mb-10  text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600 relative inline-block mx-auto">
          {translations[language].GeographyClimate}
          <span className=" center absolute -bottom-2  left-1/2 transform -translate-x-1/2 w-80 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"></span>

        </h2>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/2 animate-fadeInLeft  animation-delay-300">
            <div className="bg-white  p-6 rounded-lg shadow-md h-full border-t-4 border-amber-500 hover:shadow-xl transition duration-300">
              <h3 className="text-xl font-bold mb-4 text-gray-800 border-b border-amber-500 pb-2 inline-block">
                {translations[language].LandscapeTopography}
              </h3>
              <p className="text-gray-700 mb-4">
                {translations[language].ltp1}
              </p>
              <p className="text-gray-700 mb-4">
                {translations[language].ltp2}
              </p>
              <p className="text-gray-700">
                {translations[language].ltp3}
              </p>
            </div>
          </div>

          <div className="lg:w-1/2 animate-fadeInRight animation-delay-300">
            <div className="bg-white p-6 rounded-lg shadow-md h-full border-t-4 border-amber-500 hover:shadow-xl transition duration-300">
              <h3 className="text-xl font-bold mb-4 text-gray-800 border-b border-amber-500 pb-2 inline-block">
                {translations[language].ClimateCharacteristics}
              </h3>

              <div className="space-y-4">
                <div className="flex items-start transform transition hover:scale-105 duration-300">
                  <div className="mt-1 bg-blue-100 p-2 rounded-full">
                    <CloudRain size={24} className="text-blue-500" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold">{translations[language].Rainfall}</h4>
                    <p className="text-gray-700">{translations[language].rd}</p>
                  </div>
                </div>

                <div className="flex items-start transform transition hover:scale-105 duration-300">
                  <div className="mt-1 bg-amber-100 p-2 rounded-full">
                    <CloudRain size={24} className="text-amber-500" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold">{translations[language].Summer}</h4>
                    <p className="text-gray-700">{translations[language].sd}</p>
                  </div>
                </div>

                <div className="flex items-start transform transition hover:scale-105 duration-300">
                  <div className="mt-1 bg-blue-50 p-2 rounded-full">
                    <CloudRain size={24} className="text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold">{translations[language].Winter}</h4>
                    <p className="text-gray-700">{translations[language].wd}</p>
                  </div>
                </div>

                <div className="flex items-start transform transition hover:scale-105 duration-300">
                  <div className="mt-1 bg-green-100 p-2 rounded-full">
                    <CloudRain size={24} className="text-green-500" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold">{translations[language].Monsoon}</h4>
                    <p className="text-gray-700">{translations[language].md}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Culture & Festivals Section */}
      <section className="py-16 px-4 bg-yellow-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-10 text-center text-gray-800">
            {translations[language].CultureFestivals}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <img src="/home image/IMG_20250515_122921.jpg" alt="Ganesh Festival" className="w-full h-48 max-h-full max-w-4xl object-cover" />
              <div className="p-4">
                <h3 className="font-bold text-lg mb-2">{translations[language].ReligiousFestivals}</h3>
                <p className="text-gray-700">
                  {translations[language].ReligiousFestivalspara}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <img src="/home image/default-dance.webp" alt="Buldhana Folk Dance" className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-bold text-lg mb-2">{translations[language].FolkArtMusic}</h3>
                <p className="text-gray-700">
                  {translations[language].FolkArtMusicpara}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <img src="/home image/dishes of maharastra.avif" alt="Local Cuisine" className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-bold text-lg mb-2">{translations[language].Cuisine}</h3>
                <p className="text-gray-700">
                  {translations[language].Cuisinepara}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Economy Section */}
      <section id="economy-section" className="py-16 px-4 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-10 text-center text-gray-800">
          {translations[language].EconomyLivelihood}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center mb-4">
              <Briefcase size={24} className="text-yellow-600 mr-2" />
              <h3 className="text-xl font-bold text-gray-800">{translations[language].Agriculture}</h3>
            </div>

            <p className="text-gray-700 mb-4">
              {translations[language].ELP1}
            </p>

            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>{translations[language].Cotton}</li>
              <li>{translations[language].Soybeans}</li>
              <li>{translations[language].Pulses}</li>
              <li>{translations[language].JowarWheat}</li>
              <li>{translations[language].Fruits}</li>
            </ul>

            <p className="text-gray-700 mt-4">
              {translations[language].ELP2}
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center mb-4">
              <Briefcase size={24} className="text-yellow-600 mr-2" />
              <h3 className="text-xl font-bold text-gray-800">{translations[language].IndustryServices}</h3>
            </div>

            <p className="text-gray-700 mb-4">
              {translations[language].ELP3}
            </p>

            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>{translations[language].CottonGinning}</li>
              <li>{translations[language].AgroProcessing}</li>
              <li>{translations[language].SmallScaleIndustries}</li>
              <li>{translations[language].HandicraftsHandloom}</li>
            </ul>

            <p className="text-gray-700 mt-4">
              {translations[language].ELP4}
            </p>
          </div>
        </div>
      </section>

      {/* Administrative Setup Section */}
      <section id="admin-section" className="py-16 px-4 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-10 text-center text-gray-800">
            {translations[language].AdministrativeSetup}
          </h2>

          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-1/2">
              <div className="bg-white p-6 rounded-lg shadow-md h-full">
                <h3 className="text-xl font-bold mb-4 text-gray-800 border-b border-yellow-500 pb-2">
                  {translations[language].DistrictAdministration}
                </h3>

                <p className="text-gray-700 mb-4">
                  {translations[language].ASP1}
                </p>

                <div className="space-y-3">
                  <div className="flex items-start">
                    <Building size={20} className="text-yellow-600 mt-1 mr-2" />
                    <div>
                      <h4 className="font-semibold">{translations[language].DistrictCollectorate}</h4>
                      <p className="text-gray-700">{translations[language].ASP2}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Building size={20} className="text-yellow-600 mt-1 mr-2" />
                    <div>
                      <h4 className="font-semibold">{translations[language].ZillaParishad}</h4>
                      <p className="text-gray-700">{translations[language].ASP3}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Building size={20} className="text-yellow-600 mt-1 mr-2" />
                    <div>
                      <h4 className="font-semibold">{translations[language].MunicipalCouncils}</h4>
                      <p className="text-gray-700">{translations[language].ASP4}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:w-1/2">
              <div className="bg-white p-6 rounded-lg shadow-md h-full">
                <h3 className="text-xl font-bold mb-4 text-gray-800 border-b border-yellow-500 pb-2">
                  {translations[language].AdministrativeDivisions}
                </h3>

                <p className="text-gray-700 mb-4">
                  {translations[language].ASP5}
                </p>

                <div className="grid grid-cols-2 gap-2">
                  {tehsils.map((tehsil, index) => (
                    <div key={index} className="bg-yellow-50 px-3 py-2 rounded-md text-gray-800">
                      {tehsil}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery-section" className="py-16 px-4 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-10 text-center text-gray-800">
          {translations[language].DistrictGallery}
        </h2>

        <div className="relative">
          {/* Carousel */}
          <div className="overflow-hidden relative rounded-lg shadow-lg">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {galleryImages.map((image, index) => (
                <div key={index} className="w-full flex-shrink-0">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-80 object-cover"
                  />
                  <div className="bg-black bg-opacity-70 text-white p-3 text-center">
                    <h3 className="font-bold">{image.title}</h3>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation buttons */}
            <button
              onClick={prevSlide}
              className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
            >
              <ChevronLeft size={24} />
            </button>

            <button
              onClick={nextSlide}
              className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Indicators */}
          <div className="flex justify-center mt-4 space-x-2">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full ${currentSlide === index ? 'bg-yellow-600' : 'bg-gray-300'
                  }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}

      {/* Footer */}
      <div id="footer-section" className="h-16"></div>
      <footer className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-12 mt-auto">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center overflow-hidden">
                  <img src="\home image\buldana image copy.svg" alt="Buldhana Logo" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="text-lg font-bold">{translations[language].BuldhanaDistrict}</h4>
                  <p className="text-xs text-gray-400">{translations[language].GovernmentOfMaharashtra}</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                {translations[language].PortalTitle}
              </p>
              <div className="flex space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition transform hover:scale-110 cursor-pointer">
                  <span className="text-white text-sm"><a href='https://www.facebook.com/sharer/sharer.php?u=https://zpbuldhana.maharashtra.gov.in/en/about-department/introduction/&t=Introduction'>f</a></span>
                </div>
                <div className="w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center hover:bg-blue-500 transition transform hover:scale-110 cursor-pointer">
                  <span className="text-white text-sm"><a href='https://x.com/intent/post?url=https%3A%2F%2Fzpbuldhana.maharashtra.gov.in%2Fen%2Fabout-department%2Fintroduction%2F&via=TWITTER_HANDLE&text=Introduction'>X</a></span>
                </div>
                {/* <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition transform hover:scale-110 cursor-pointer">
                  <span className="text-white text-sm">yt</span>
                </div> */}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 relative inline-block">
                {translations[language].QuickLinks}
                <div className="absolute -bottom-1 left-0 w-12 h-0.5 bg-yellow-500"></div>
              </h4>
              <ul className="text-sm space-y-3 text-gray-300">
                {[
                  { name: <span>{translations[language].Home}</span>, href: '/' },
                  { name: <span>{translations[language].About}</span>, href: '/AboutBuldhanaPage' },
                  { name: <span>{translations[language].Economy}</span>, href: '#economy-section' },
                  { name: <span>{translations[language].Administration}</span>, href: '#admin-section' },
                  { name: <span>{translations[language].Gallery}</span>, href: '#gallery-section' },
                  { name: <span>{translations[language].Contact}</span>, href: '#footer-section' },
                ].map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="hover:text-yellow-400 cursor-pointer transform hover:translate-x-1 transition-transform duration-300 flex items-center"
                    >
                      <ChevronRight size={14} className="mr-1 text-yellow-500" />
                      <span>{link.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>


            <div>
              <h4 className="text-lg font-bold mb-4 relative inline-block">
                {translations[language].ImportantResources}
                <div className="absolute -bottom-1 left-0 w-12 h-0.5 bg-yellow-500"></div>
              </h4>
              <ul className="text-sm space-y-3 text-gray-300">
                {[
                  <a href='https://www.maharashtra.gov.in/'>{translations[language].MaharashtraGovernment}</a>,
                  <a href='https://sahajpranali.safevaults.in/en/login/public'>{translations[language].Sahajpranali}</a>,
                  <a href='https://jigaon.in/'>{translations[language].Jigaon}</a>,
                  <a href='https://amravatidivision.gov.in/'>{translations[language].AmravatiDivision}</a>,
                  <a href='https://aaplesarkar.mahaonline.gov.in/en'>{translations[language].AapleSarkar}</a>
                ].map((resource, index) => (
                  <li key={index} className="hover:text-yellow-400 cursor-pointer transform hover:translate-x-1 transition-transform duration-300 flex items-center">
                    <ChevronRight size={14} className="mr-1 text-yellow-500" />
                    <span>{resource}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4 relative inline-block">
                {translations[language].OfficeHours}
                <div className="absolute -bottom-1 left-0 w-12 h-0.5 bg-yellow-500"></div>
              </h4>
              <div className="flex items-center space-x-2 mb-3 text-gray-300 text-sm">
                <Clock size={18} className="text-yellow-400" />
                <span>{translations[language].MondaytoFriday}: 10:00 AM - 5:30 PM</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <Clock size={18} className="text-yellow-400" />
                <span>{translations[language].Saturday}: 10:00 AM - 2:00 PM</span>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <p className="text-center md:text-left text-sm text-gray-400">
              © 2025 Government of Maharashtra, Buldhana District. All Rights Reserved.
            </p>
            <div className="flex items-center justify-center md:justify-end space-x-6">
              <a href="#" className="text-sm text-gray-400 hover:text-white transition">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-400 hover:text-white transition">Terms of Use</a>
              <a href="#" className="text-sm text-gray-400 hover:text-white transition">Sitemap</a>
            </div>
          </div>

          {/* Back to top button */}
          <div className="fixed bottom-6 right-6 z-20">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-yellow-500 hover:bg-yellow-600 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition transform hover:scale-110"
              aria-label="Back to top"
            >
              <ChevronUp size={20} />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
